import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import { BankBilletState } from "store/reducers/bankBillet";
import IBankBillet, { BankBilletStatus } from "Types/BankBillet";
import dayjs from "dayjs";
import Report from "Components/Report";
import { dateSort } from "Components/Report/helpers";
import { toStringCurrency } from "helpers/string";

import ImportModal from "./Import";
import ReactTable from "react-table";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { MdAdd } from "react-icons/md";
import { Fab } from "@mui/material";
import useStyles from "./styles";

const columns = [
	{
		Header: ls.creationDate,
		id: "createdAt",
		accessor: (c: IBankBillet) =>
			dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds),
		show: true,
		sortMethod: dateSort,
		width: 150
	},
	{
		Header: "Nº Documento",
		id: "document",
		show: true,
		accessor: (c: IBankBillet) => c.referenceNumber
	},
	{
		Header: ls.name,
		id: "name",
		show: true,
		accessor: (c: IBankBillet) => c.name
	},
	{
		Header: ls.email,
		id: "email",
		show: true,
		accessor: (c: IBankBillet) => c.email,
		width: 250
	},
	{
		Header: ls.value,
		id: "value",
		show: true,
		filterable: false,
		accessor: (c: IBankBillet) => toStringCurrency(+c.value / 100.0),
		width: 150
	},
	{
		Header: ls.status,
		id: "status",
		show: true,
		accessor: (c: IBankBillet) => ls[BankBilletStatus[c.status]],
		width: 140
	},
	{
		Header: ls.dueDate,
		id: "dueDate",
		accessor: (c: IBankBillet) => c.dueDate,
		show: true,
		sortMethod: dateSort
	},
	{
		Header: "Cód. Barras",
		id: "barCode",
		accessor: (c: IBankBillet) => c.barCode,
		show: true
	},
	{
		Header: ls.actions,
		id: "actions",
		accessor: (c: any) => (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flex: 1
				}}
			>
				<a href={c.url} target="_blank">
					<FaExternalLinkAlt />
				</a>
			</div>
		),
		show: true,
		filterable: false,
		width: 80
	}
];

let getBankBilletsDebounded: (() => any) & _.Cancelable;

interface TableState {
    page: number;
    pageSize: number;
    sorted: any[]; // Defina o tipo correto para sorted
    filtered: any[]; // Defina o tipo correto para filtered
    toExport: boolean;
    callback: any; // Defina o tipo correto para callback
}

function BankBillets() {
	const navigate = useNavigate();
	const dispatch = useDispatch<any>();
	const table: any = useRef<ReactTable<any>>(null);
	const classes = useStyles({});

	const state = useSelector<State, BankBilletState>(s => s.bankBillet);

	const handleFetchData = (tableState: TableState, instance: any) => {
		if (!tableState) return;
		const { page, pageSize, sorted, filtered, toExport, callback } = tableState;

		if (getBankBilletsDebounded) {
			getBankBilletsDebounded.cancel();
		}

		if (!sorted) tableState.sorted = [];

		const creationDate = filtered.find((c: any) => c.id === "creationDate");

		if (creationDate?.value) {
			tableState.filtered = filtered.filter((c: any) => c.id !== "creationDate");

			if (creationDate?.value.startDate) {
				filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate)
				filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
		}

		getBankBilletsDebounded = _.debounce(
			() =>
				dispatch(
					actions.bankBillet.getBankBillets(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getBankBilletsDebounded();
	};

	const load = () => handleFetchData(table.current?.state, null);

	return (
		<div>
			<Helmet>
				<title>
					{ls.bankBillets} | {ls.appName}
				</title>
			</Helmet>
			<Report
				manual
				tableRef={table}
				headerRightComponent={<ImportModal load={load} />}
				title={ls.bankBillets}
				data={state.items}
				pages={state.pages}
				loading={state.loading.getAll}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				visibleColumns={columns.filter(c => c.show).map(c => c.Header)}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
				onRowClicked={data => navigate(`/Dashboard/BankBillet/${data._id}`)}
			/>

			<Fab
				color="primary"
				aria-label="Add"
				style={{ position: 'fixed', bottom: '16px', right: '16px',  zIndex: 1000}}
				onClick={() => navigate("/Dashboard/BankBillet/Create")}
			>
				<MdAdd size={24} />
			</Fab>
		</div>
	);
}

export default BankBillets;
